import { get, del } from '../api.service';

export interface ValidateVatResponse {
  statusCode: number;
  message: string;
  response: {
    vatNumber: string;
    isValid: boolean;
    registrationData?: {
      name?: string;
      address?: string;
      city?: string;
      zip?: string;
      countryIso?: string;
      countryName?: string;
    };
  };
}

export interface Customer {
  customerId?: number;
  name: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  address?: string;
  address2?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  countryCode?: string;
  reference?: string;
  orgNo?: string;
  accountType?: number;
  ssn?: string;
  co?: string | null;
  isActive?: boolean;
  fortnoxCustomerId?: string;
  vatNumber?: string;
  paymentTerm?: string;
  language?: string;
  isAddedOnFortnox?: boolean;
  isDetailsUpdatedOnFortnox?: boolean;
  isVATRegistred?: boolean;
  isVatVerified?: boolean;
  invoiceType?: number | null;
  hasAnyInvoice?: boolean;
  isCustomerUploadedToComplyCube?: boolean | null;
  customerAddress?: any[];
  secondaryEmailAddress?: string | null;
  isNotes?: boolean;
}
export interface Freelancer {
  id?: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  reference?: string;
  signUpUser: {
    userId?: number;
    firstName?: string;
    email?: string;
    phoneNo?: string;
    organisation?: string;
    status?: string;
    isActive?: boolean;
  };
  orgNo?: string;
  accountType?: number;
  ssn?: string;
}

export interface CustomerParams {
  searchQuery?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface CustomerResponse {
  statusCode: number;
  message: string;
  response: Customer[];
  totalCount?: number;
  numberOfPages?: number;
}

/**
 * Fetches customers from the API with optional search and pagination.
 * Corresponds to: GET /api/customercontrollerv2?searchQuery=query&pageNumber=1&pageSize=10
 * @param params - The request parameters containing searchQuery, pageNumber, and pageSize.
 * @returns The API response with customer data.
 */
export const fetchCustomers = async (params?: CustomerParams): Promise<CustomerResponse> => {
  try {
    const response = await get('/customerv2', {
      ...(params?.searchQuery && { searchQuery: params.searchQuery }),
      ...(params?.pageNumber && { pageNumber: params.pageNumber }),
      ...(params?.pageSize && { pageSize: params.pageSize }),
    });
    return response;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

/**
 * Fetches a single customer by ID.
 * @param customerId - The ID of the customer to fetch.
 * @returns The API response with customer data.
 */
export const fetchCustomerById = async (customerId: number): Promise<Customer> => {
  try {
    const response = await get(`/customer/${customerId}`);
    // If response is wrapped in a response object, extract it
    return response.response || response;
  } catch (error) {
    console.error(`Error fetching customer with ID ${customerId}:`, error);
    throw error;
  }
};

/**
 * Validates a VAT number via the backend API.
 * Corresponds to: GET /api/customer/validate-vat/{vatNumber}
 * @param vatNumber - The full VAT number (including country code prefix).
 * @returns The API response with isValid flag.
 */
export const validateVatNumberApi = async (vatNumber: string): Promise<ValidateVatResponse> => {
  const response = await get(`/customer/validate-vat/${encodeURIComponent(vatNumber)}`);
  return response;
};

/**
 * Deletes a customer by ID.
 * @param customerId - The ID of the customer to delete.
 * @returns The API response.
 */
export const deleteCustomer = async (customerId: number): Promise<any> => {
  try {
    const response = await del(`/customer/${customerId}`);
    return response;
  } catch (error: any) {
    console.error('Delete customer API error:', error);
    throw new Error(
      error?.response?.data?.message ||
      error?.message ||
      'Failed to delete customer'
    );
  }
};
